import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'

interface ReplayGraphSVGProps {
  data: number[]
  height?: number
}

interface GraphSize {
  width: number
  height: number
}

interface GraphPoint {
  x: number
  y: number
  value: number
  index: number
}

const ReplayGraphSVG: React.FC<ReplayGraphSVGProps> = ({
  data,
  height = 75
}) => {
  const svgRef = useRef<SVGSVGElement>(null)

  const [size, setSize] = useState<GraphSize>({
    width: 0,
    height
  })

  useLayoutEffect(() => {
    const svg = svgRef.current

    if (!svg) {
      return
    }

    const updateSize = () => {
      const rect = svg.getBoundingClientRect()

      setSize(previous => {
        const nextWidth = Math.max(
          rect.width,
          1
        )

        const nextHeight = Math.max(
          rect.height,
          1
        )

        if (
          Math.abs(
            previous.width - nextWidth
          ) < 0.5 &&
          Math.abs(
            previous.height - nextHeight
          ) < 0.5
        ) {
          return previous
        }

        return {
          width: nextWidth,
          height: nextHeight
        }
      })
    }

    updateSize()

    const resizeObserver =
      new ResizeObserver(
        updateSize
      )

    resizeObserver.observe(svg)

    return () => {
      resizeObserver.disconnect()
    }
  }, [data.length, height])

  /*
   * Dots are based on meaningful movement rather
   * than a fixed every-N-points rule.
   */
  const markerIndexes = useMemo(() => {
    if (!data.length) {
      return new Set<number>()
    }

    const markers =
      new Set<number>()

    markers.add(0)
    markers.add(data.length - 1)

    const maxValue =
      Math.max(...data, 1)

    /*
     * Smaller percentage = more dots.
     *
     * We still clamp to at least 1 because these
     * are integer replay counts.
     */
    const changeThreshold =
      Math.max(
        1,
        maxValue * 0.08
      )

    let lastMarkedValue =
      data[0]

    for (
      let index = 1;
      index < data.length - 1;
      index++
    ) {
      const previous =
        data[index - 1]

      const current =
        data[index]

      const next =
        data[index + 1]

      const isPeak =
        current > previous &&
        current >= next

      const isValley =
        current < previous &&
        current <= next

      const changedEnough =
        Math.abs(
          current -
          lastMarkedValue
        ) >= changeThreshold

      /*
       * These preserve meaningful changes when a
       * plateau starts or ends.
       */
      const startsPlateau =
        current !== previous &&
        current === next

      const endsPlateau =
        current === previous &&
        current !== next

      if (
        isPeak ||
        isValley ||
        changedEnough ||
        startsPlateau ||
        endsPlateau
      ) {
        markers.add(index)

        lastMarkedValue =
          current
      }
    }

    return markers
  }, [data])

  /*
   * Vertical guides are intentionally more regular
   * than the circles.
   */
  const guideInterval = useMemo(() => {
    if (data.length <= 12) {
      return 1
    }

    if (data.length <= 25) {
      return 2
    }

    if (data.length <= 50) {
      return 4
    }

    if (data.length <= 100) {
      return 8
    }

    return Math.max(
      1,
      Math.ceil(
        data.length / 12
      )
    )
  }, [data.length])

  const {
    points,
    pathData,
    areaPathData,
    graphHeight
  } = useMemo(() => {
    if (
      data.length === 0 ||
      size.width <= 0
    ) {
      return {
        points: [] as GraphPoint[],
        pathData: '',
        areaPathData: '',
        graphHeight: size.height
      }
    }

    const currentGraphHeight =
      size.height

    const maxValue =
      Math.max(...data, 0)

    /*
     * Do NOT stretch every dataset to fill the
     * whole graph vertically.
     *
     * A replay count of 1 should look subtle.
     * Higher replay counts gradually get more
     * visual height.
     */
    const maxVisualHeightRatio =
      maxValue <= 0
        ? 0
        : Math.min(
            0.82,
            0.14 +
              Math.log10(
                maxValue + 1
              ) *
                0.27
          )

    const visualHeight =
      currentGraphHeight *
      maxVisualHeightRatio

    const safeMaxValue =
      Math.max(
        maxValue,
        1
      )

    const stepX =
      data.length > 1
        ? size.width /
          (data.length - 1)
        : 0

    const graphPoints: GraphPoint[] =
      data.map(
        (
          value,
          index
        ) => {
          /*
           * Normalize against absolute replay
           * volume rather than local min/max.
           *
           * This is what prevents:
           *
           * 1 -> 0
           *
           * from looking as dramatic as:
           *
           * 50 -> 0
           */
          const normalized =
            value /
            safeMaxValue

          const y =
            currentGraphHeight -
            normalized *
              visualHeight

          return {
            x:
              index *
              stepX,
            y,
            value,
            index
          }
        }
      )
const smoothPoints = (
  items: GraphPoint[],
  radius = 4
): GraphPoint[] => {
  return items.map((point, index) => {
    let weightedY = 0
    let totalWeight = 0

    for (let offset = -radius; offset <= radius; offset++) {
      const neighborIndex = index + offset

      if (neighborIndex < 0 || neighborIndex >= items.length) {
        continue
      }

      const distance = offset / radius
      const weight = Math.exp(-0.5 * distance * distance * 4)

      weightedY += items[neighborIndex].y * weight
      totalWeight += weight
    }

    return {
      ...point,
      y: weightedY / totalWeight
    }
  })
}
    /*
     * Smooth cubic curve.
     *
     * Every backend point still influences the
     * curve, but the transitions are rounded
     * instead of sharp L-to-L corners.
     */
    const buildPath = (
      items: GraphPoint[]
    ) => {
      if (!items.length) {
        return ''
      }

      if (
        items.length === 1
      ) {
        return (
          `M ${items[0].x},` +
          `${items[0].y}`
        )
      }

      let path =
        `M ${items[0].x},` +
        `${items[0].y}`

      for (
        let index = 1;
        index < items.length;
        index++
      ) {
        const previous =
          items[index - 1]

        const current =
          items[index]

        const midpointX =
          (
            previous.x +
            current.x
          ) /
          2

        path +=
          ` C ` +
          `${midpointX},${previous.y} ` +
          `${midpointX},${current.y} ` +
          `${current.x},${current.y}`
      }

      return path
    }

const smoothedPoints = smoothPoints(graphPoints, 5)

const linePath = buildPath(smoothedPoints)

    const areaPath =
      linePath
        ? `${linePath} ` +
          `L ${size.width},${currentGraphHeight} ` +
          `L 0,${currentGraphHeight} Z`
        : ''

return {
  points: smoothedPoints,
  pathData: linePath,
  areaPathData: areaPath,
  graphHeight: currentGraphHeight
}
  }, [
    data,
    size
  ])

  if (
    data.length === 0
  ) {
    return null
  }

  return (
    <svg
      ref={svgRef}
      viewBox={
        `0 0 ` +
        `${Math.max(
          size.width,
          1
        )} ` +
        `${Math.max(
          size.height,
          1
        )}`
      }
      width='100%'
      height={height}
      preserveAspectRatio='none'
      style={{
        display: 'block',
        width: '100%',
        height,
        background:
          'transparent',
        overflow:
          'visible'
      }}
    >
      {areaPathData && (
        <path
          d={
            areaPathData
          }
          fill='rgba(0, 0, 0, 0.08)'
        />
      )}

      {pathData && (
        <path
          d={pathData}
          fill='none'
          stroke='white'
          strokeWidth={
            1
          }
          strokeOpacity={0.3}
          strokeLinecap='round'
          strokeLinejoin='round'
          vectorEffect='non-scaling-stroke'
        />
      )}

      {/*
       * Regular vertical guides.
       *
       * These are independent from the circle
       * markers, so you'll always have consistent
       * visual segmentation.
       */}
      {points.map(
        (
          point,
          index
        ) => {
          const showGuide =
            index %
              guideInterval ===
              0 ||
            index ===
              points.length -
                1

          if (
            !showGuide
          ) {
            return null
          }

          return (
            <line
              key={
                `guide-${index}`
              }
              x1={
                point.x
              }
              y1={
                point.y +
                2
              }
              x2={
                point.x
              }
              y2={
                graphHeight
              }
              stroke='white'
              strokeWidth={
                1
              }
              strokeOpacity={
                0.1
              }
              vectorEffect='non-scaling-stroke'
            />
          )
        }
      )}

      {/*
       * Circles only at meaningful changes.
       */}
{points.map((point, index) => {
  const showCircle =
    index % guideInterval === 0 ||
    index === points.length - 1

  if (
    !showCircle ||
    point.value <= 0
  ) {
    return null
  }

  return (
    <circle
      key={`dot-${index}`}
      cx={point.x}
      cy={point.y}
      r={2}
      fill='white'
      fillOpacity={1}
    />
  )
})}
    </svg>
  )
}

export default ReplayGraphSVG